import { useState, useEffect, useRef } from 'react';
import type { IAsset } from '../providers';
import { formatDate } from '../utils';

import { useAssetActions } from '../context/AssetActionsContext';
import { useLabels, useWorkflowStatuses } from '../hooks';
import { ZprLabel } from '@zpr-stencil';
import { useMode } from '../context/ModeContext';

export interface AssetTableProps {
  /** Array of media assets to display */
  assets: IAsset[];
  /** Currently selected asset IDs */
  selectedAssets: string[];
  /** Callback when an asset is clicked */
  onAssetClick?: (asset: IAsset) => void;
  /** Callback when an asset is selected/deselected */
  onAssetSelect?: (assetId: string) => void;
  /** Enable multi-selection of assets */
  enableMultiSelect?: boolean;
  /** IDs visually highlighted by an in-progress drag selection */
  dragHighlightedIds?: string[];
  /** Whether a drag is currently in progress (suppresses row clicks) */
  isDragActive?: boolean;
  /** Custom CSS class */
  className?: string;
}

export const AssetTable = ({
  assets,
  selectedAssets = [],
  onAssetClick,
  onAssetSelect,
  enableMultiSelect,
  dragHighlightedIds = [],
  isDragActive = false,
  className = '',
}: AssetTableProps) => {
  const [previewUrls, setPreviewUrls] = useState<{ [key: string]: string }>({});
  const { zapworksProvider, workspaceId } = useAssetActions();
  const { getStatusNameByUid } = useWorkflowStatuses(zapworksProvider, workspaceId);
  const { getLabelNameByUid, getLabelObjectByUid } = useLabels(zapworksProvider, workspaceId);
  const { premium } = useMode();

  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);
  const dragJustEndedRef = useRef(false);

  // Calculate select-all checkbox state
  const allSelected = assets.length > 0 && selectedAssets.length === assets.length;
  const someSelected = selectedAssets.length > 0 && selectedAssets.length < assets.length;

  // Set indeterminate state when some assets are selected
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  useEffect(() => {
    // Load preview URLs for all assets
    const loadPreviews = async () => {
      const urls: { [key: string]: string } = {};
      for (const asset of assets) {
        try {
          const url = await asset.getImageUrlForCard({ width: 48, height: 48 });
          urls[asset.getId()] = url;
        } catch (error) {
          console.error(`Failed to load preview for ${asset.getId()}:`, error);
        }
      }
      setPreviewUrls(urls);
    };
    loadPreviews();
  }, [assets]);

  const handleRowClick = (asset: IAsset) => {
    // Suppress click events that are the tail-end of a drag selection
    if (dragJustEndedRef.current) return;

    if (onAssetSelect) {
      onAssetSelect(asset.getId());
    }
    if (onAssetClick) {
      onAssetClick(asset);
    }
  };

  // Track isDragActive transitions to set the click guard
  const prevDragActive = useRef(isDragActive);
  if (prevDragActive.current && !isDragActive) {
    dragJustEndedRef.current = true;
    requestAnimationFrame(() => {
      dragJustEndedRef.current = false;
    });
  }
  prevDragActive.current = isDragActive;

  const handleCheckboxChange = (assetId: string) => {
    onAssetSelect?.(assetId);
  };

  const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecking = e.target.checked;
    if (isChecking) {
      // Select all unselected assets
      const unselectedAssets = assets.filter(asset => !selectedAssets.includes(asset.getId()));
      unselectedAssets.forEach(asset => onAssetSelect?.(asset.getId()));
    } else {
      // Deselect all selected assets
      selectedAssets.forEach(id => onAssetSelect?.(id));
    }
  };

  return (
    <div className={`asset-table-container ${className}`}>
      <table className="asset-table">
        <colgroup>
          {enableMultiSelect && <col style={{ width: '40px' }} />}
          <col style={{ width: 'auto' }} />
          <col style={{ width: '150px' }} />
          {premium && <col style={{ width: '150px' }} />}
        </colgroup>
        <thead>
          <tr>
            {enableMultiSelect && (
              <th className="asset-table__checkbox-column">
                <input
                  ref={selectAllCheckboxRef}
                  type="checkbox"
                  aria-label="Select all assets"
                  checked={allSelected}
                  onChange={handleSelectAllChange}
                />
              </th>
            )}
            <th>Name</th>
            <th>Created At</th>
            {premium && ( <th>Status</th> )}
          </tr>
        </thead>
        <tbody>
          {assets.map((asset) => {
            const metadata = asset.getMetadata();
            const isSelected = selectedAssets.includes(asset.getId());
            const isDragHighlighted = dragHighlightedIds.includes(asset.getId());
            const previewUrl = previewUrls[asset.getId()];
            const labels = Array.isArray(metadata.labels) ? metadata.labels.map((l: any) => l.uid as string) : [];
            const date = metadata.date ? new Date(metadata.date) : null;
            const workflowStatusId = metadata.workflowStatusId as string || 'unknown';
            const workflowStatus = getStatusNameByUid(workflowStatusId);

            return (
              <tr
                key={asset.getId()}
                data-asset-id={asset.getId()}
                className={`asset-table__row ${isSelected ? 'selected' : ''} ${isDragHighlighted ? 'asset-table__row--drag-highlighted' : ''}`}
                onClick={() => handleRowClick(asset)}
              >
                {enableMultiSelect && (
                  <td className="asset-table__checkbox-column">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleCheckboxChange(asset.getId())}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select ${asset.getName()}`}
                    />
                  </td>
                )}
                <td className="asset-table__preview-column">
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt={asset.getName()}
                      className="asset-table__preview-image"
                      width={48}
                      height={48}
                    />
                  ) : (
                    <div className="asset-table__preview-placeholder" />
                  )}
                  <div className="asset-table__name">
                    <p className="asset-table__name-text" title={asset.getName()}>
                      {asset.getName()}
                    </p>
                    {labels.length > 0 && (
                      <div className="asset-table__labels">
                        {labels.map((labelUid, index) => (
                          <ZprLabel
                            size='small'
                            key={labelUid}
                            text={getLabelNameByUid(labelUid)}
                            color={getLabelObjectByUid(labelUid)?.color}
                            ></ZprLabel>
                        ))}
                      </div>
                    )}
                  </div>
                </td>
                <td className="asset-table__date-column">
                  {date ? formatDate(date) : 'Unknown'}
                </td>
                {premium && (
                  <td className="asset-table__status-column">
                    {workflowStatus}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};